import React, { createContext, useContext, useState, useEffect } from 'react';
import { checkImageFormatSupport, getOptimizedImageUrl, generateTinyPlaceholder } from '../utils/imageUtils';

// Create the context
const ImageOptimizationContext = createContext({
  formatSupport: { webp: false, avif: false, jpeg2000: false },
  getOptimizedSrc: (src, width) => src,
  generatePlaceholder: (src) => Promise.resolve(null),
  placeholderCache: {},
  isSupported: false,
  isDetecting: true
});

/**
 * ImageOptimizationProvider component
 * Provides image optimization capabilities to the entire app
 */
export const ImageOptimizationProvider = ({ children }) => {
  const [formatSupport, setFormatSupport] = useState({ 
    webp: false, 
    avif: false, 
    jpeg2000: false 
  });
  const [isDetecting, setIsDetecting] = useState(true);
  const [placeholderCache, setPlaceholderCache] = useState({});

  // Check for browser support of modern image formats on mount
  useEffect(() => {
    const detectSupport = async () => {
      try {
        const support = await checkImageFormatSupport();
        setFormatSupport(support);
      } catch (error) {
        console.warn('Failed to detect image format support:', error);
      } finally {
        setIsDetecting(false);
      }
    };

    detectSupport();
  }, []);

  /**
   * Get optimized image source based on browser capabilities
   */
  const getOptimizedSrc = (src, width = 800) => {
    return getOptimizedImageUrl(src, width, formatSupport);
  };

  /**
   * Generate and cache placeholders for faster repeat access
   */
  const generatePlaceholder = async (src) => {
    if (!src) return null;
    
    // Return cached placeholder if available
    if (placeholderCache[src]) {
      return placeholderCache[src];
    }
    
    try {
      const placeholder = await generateTinyPlaceholder(src);
      
      // Update cache
      setPlaceholderCache(prev => ({
        ...prev,
        [src]: placeholder
      }));
      
      return placeholder;
    } catch (error) {
      console.warn('Failed to generate placeholder:', error);
      return null;
    }
  };

  // Check if any modern format is supported
  const isSupported = formatSupport.webp || formatSupport.avif;

  // Context value
  const value = {
    formatSupport,
    getOptimizedSrc,
    generatePlaceholder,
    placeholderCache,
    isSupported,
    isDetecting
  };

  return (
    <ImageOptimizationContext.Provider value={value}>
      {children}
    </ImageOptimizationContext.Provider>
  );
};

/**
 * Custom hook to use image optimization features
 */
export const useImageOptimization = () => {
  const context = useContext(ImageOptimizationContext);
  
  if (context === undefined) {
    throw new Error('useImageOptimization must be used within an ImageOptimizationProvider');
  }
  
  return context;
};

export default ImageOptimizationContext; 